import React, { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { AtiaBrand, RoleBadge } from "./Atoms";
import { House, ClipboardText, SignOut, Buildings, UserList, ChartLineUp, FilePlus, FolderUser, MapTrifold, CalendarCheck, Calendar, User as UserIcon, FolderSimpleLock, ShieldCheck, Wallet, Handshake, Palette } from "@phosphor-icons/react";

const ROLE_NAV = {
    supervisor: [
        { path: "/dashboard", label: "Panel", icon: House },
        { path: "/agenda", label: "Agenda", icon: Calendar },
        { path: "/orders", label: "Órdenes", icon: ClipboardText },
        { path: "/inspections", label: "Inspecciones", icon: ChartLineUp },
        { path: "/map", label: "Mapa", icon: MapTrifold },
        { path: "/clients", label: "Clientes", icon: Buildings },
        { path: "/servicios-diseno", label: "Diseño", icon: Palette },
        { path: "/technicians", label: "Técnicos", icon: UserList },
        { path: "/supervision", label: "Supervisión", icon: FolderUser },
    ],
    comercial: [
        { path: "/dashboard", label: "Panel", icon: House },
        { path: "/clients", label: "Clientes + Planes", icon: Buildings },
        { path: "/servicios-diseno", label: "Diseño", icon: Palette },
        { path: "/orders", label: "Órdenes", icon: ClipboardText },
        { path: "/agenda", label: "Agenda", icon: Calendar },
        { path: "/mi-comision", label: "Mi comisión", icon: Handshake },
        { path: "/map", label: "Mapa", icon: MapTrifold },
    ],
    tecnico: [
        { path: "/dashboard", label: "Mi día", icon: House },
        { path: "/agenda", label: "Mi agenda", icon: Calendar },
        { path: "/orders", label: "Mis órdenes", icon: ClipboardText },
        { path: "/inspections", label: "Mis inspecciones", icon: ChartLineUp },
        { path: "/mis-contratos", label: "Mis contratos", icon: Wallet },
        { path: "/map", label: "Mapa", icon: MapTrifold },
    ],
    cliente: [
        { path: "/dashboard", label: "Mi panel", icon: UserIcon },
        { path: "/inspections", label: "Reportes", icon: ChartLineUp },
        { path: "/map", label: "Mapa", icon: MapTrifold },
    ],
};

export default function Topbar() {
    const { user, logout } = useAuth();
    const loc = useLocation();
    const navigate = useNavigate();
    const [sgiStatus, setSgiStatus] = useState(null);
    const [openingSgi, setOpeningSgi] = useState(false);

    useEffect(() => {
        if (!user) return;
        api.get("/sgi/status").then(r => setSgiStatus(r.data)).catch(() => setSgiStatus(null));
    }, [user?.user_id]);

    const openSgi = async () => {
        if (openingSgi) return;
        setOpeningSgi(true);
        try {
            const r = await api.post("/sgi/access", {});
            window.open(r.data.url, "_blank", "noopener,noreferrer");
        } catch (e) {
            alert(e.response?.data?.detail || "No se puede abrir la documentación SGI");
        }
        setOpeningSgi(false);
    };

    if (!user) return null;
    const items = [...(ROLE_NAV[user.role] || ROLE_NAV.tecnico)];
    if (user.is_admin) {
        items.push({ path: "/financiero", label: "Financiero", icon: Wallet });
        items.push({ path: "/admin/users", label: "Admin", icon: ShieldCheck });
    }
    const showSgiBtn = !!sgiStatus?.available;
    const sgiTitle = sgiStatus?.reason === "plan_activo"
        ? "Sistema de Gestión Integrado (Drive)"
        : sgiStatus?.reason === "staff"
            ? "SGI corporativo (staff)"
            : "Requiere suscripción activa";
    const link = (it) => {
        const active = loc.pathname === it.path;
        const Icon = it.icon;
        return (
            <Link
                key={it.path} to={it.path}
                data-testid={`nav-${it.path.replace(/\//g, '-')}`}
                className={`flex items-center gap-2 px-3 py-2 text-sm font-medium border-b-2 transition-colors duration-200 ${
                    active ? "border-teal-500 text-zinc-900" : "border-transparent text-zinc-600 hover:text-zinc-900"
                }`}
                style={active ? { borderColor: "#14B8A6" } : {}}
            >
                <Icon size={16} weight={active ? "bold" : "regular"} />
                {it.label}
            </Link>
        );
    };

    return (
        <header className="bg-white border-b border-zinc-200 sticky top-0 z-50" data-testid="topbar">
            <div className="max-w-7xl mx-auto px-4 md:px-8 flex items-center justify-between h-16">
                <div className="flex items-center gap-8">
                    <Link to="/dashboard" data-testid="brand-link"><AtiaBrand /></Link>
                    <nav className="hidden md:flex items-center gap-1">{items.map(link)}</nav>
                </div>
                <div className="flex items-center gap-3">
                    {showSgiBtn && (
                        <button onClick={openSgi} disabled={openingSgi} title={sgiTitle}
                            className="hidden sm:flex items-center gap-2 px-3 py-1.5 text-xs font-semibold border transition-colors"
                            style={{ borderColor: "#0F766E", color: "#0F766E", background: "#F0FDFA" }}
                            data-testid="sgi-drive-btn">
                            <FolderSimpleLock size={14} weight="fill" />
                            {openingSgi ? "Abriendo…" : "Documentación SGI"}
                        </button>
                    )}
                    <div className="hidden sm:flex items-center gap-3 pl-3">
                        <div className="text-right">
                            <div className="text-xs font-semibold leading-tight">{user.name}</div>
                            <div className="flex items-center justify-end gap-2 mt-0.5">
                                <RoleBadge role={user.role} />
                                {user.is_admin && <span className="text-[10px] font-mono font-bold px-1.5 py-0.5 border border-amber-500 bg-amber-50 text-amber-800" data-testid="admin-badge">ADMIN</span>}
                                {user.cedula && <span className="text-[10px] text-zinc-500 font-mono">CC {user.cedula}</span>}
                            </div>
                        </div>
                        <div className="w-9 h-9 flex items-center justify-center text-xs font-bold text-white" style={{ background: "#14B8A6" }} data-testid="user-avatar">
                            {user.name?.[0]?.toUpperCase() || "U"}
                        </div>
                    </div>
                    <button onClick={logout} className="text-zinc-500 hover:text-zinc-900 transition-colors" data-testid="logout-btn" title="Cerrar sesión">
                        <SignOut size={20} />
                    </button>
                </div>
            </div>
            {/* Mobile nav */}
            <nav className="md:hidden flex overflow-x-auto border-t border-zinc-200 bg-zinc-50">
                {items.map(link)}
            </nav>
        </header>
    );
}
